import "../styles/Navbar.css";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { clearUserData, getUserData } from "../utils/authStorage";
import { Button } from "primereact/button";
import { proceedMerchantsStatus } from "../api/api";
import { FaArrowLeft, FaUserCircle } from "react-icons/fa";
import { FiUser, FiLogOut, FiChevronDown, FiBell, FiHelpCircle, FiGrid, FiSun, FiMoon, FiX } from "react-icons/fi";
import api, { updateMessage210, updateNacType } from "../api/api"; // import your axios instance
import { useAuth } from "../context/AuthContext";
import { getInbox, getCurrentIdentity, ROLE } from "../api/services/supportTicketsService";

const Navbar = ({ title, showBack = false }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isLightTheme, setIsLightTheme] = useState(() => localStorage.getItem("portalTheme") === "light");
  const [ticketAlert, setTicketAlert] = useState(0); // count of open inbox tickets

  /* Poll the support inbox so the bell blinks when a ticket comes in.
     Only inbox roles (super admin / org admin) receive tickets. */
  useEffect(() => {
    const identity = getCurrentIdentity();
    const hasInbox =
      identity.role === ROLE.SUPER_ADMIN || identity.role === ROLE.ORG_ADMIN;
    if (!hasInbox) return undefined;

    let cancelled = false;
    const check = async () => {
      try {
        const items = await getInbox();
        if (cancelled) return;
        setTicketAlert(items.filter((t) => t.status === "Open").length);
      } catch {
        /* ignore transient failures; keep the last known count */
      }
    };
    check();
    const timer = setInterval(check, 45000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (isLightTheme) {
      document.body.classList.add("theme-light");
      localStorage.setItem("portalTheme", "light");
    } else {
      document.body.classList.remove("theme-light");
      localStorage.setItem("portalTheme", "dark");
    }
  }, [isLightTheme]);

  const userData = getUserData();

  const user = userData?.user || userData || {};
  const userType = user.user_type;
  const userName = userData?.user?.name || userData?.email || "User";
  const initials = (name) =>
    name
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "??";
  const typeLabel = {
    org: "Organization",
    subordinate: "SubOrdinate",

    admin: "Super Admin",
  };

  const handleThemeToggle = () => {
    setIsLightTheme((prev) => !prev);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewProfile = () => {
    setDropdownOpen(false);
    navigate("/dashboard/profile");
  };

  const handleLogoutClick = () => {
    setDropdownOpen(false);
    setLogoutOpen(true);
  };

  const handleCloseLogout = () => setLogoutOpen(false);

  const handleConfirmLogout = () => {
    localStorage.removeItem("token");
    clearUserData();
    logout();
    setLogoutOpen(false);
    navigate("/");
  };

  return (
    <nav className="navbar">
      {/* LEFT SIDE */}
      <div className="navbar-left">
        {showBack ? (
          <>
            <FaArrowLeft
              title="Back"
              className="back-btn"
              onClick={handleBack}
            />
            <h1 className="navbar-title">{title}</h1>
          </>
        ) : null}
      </div>

      {/* RIGHT SIDE */}
      <div className="navbar-right">
        <div className="navbar-actions">
          <button
            className="navbar-themebtn"
            type="button"
            onClick={handleThemeToggle}
            aria-label="Toggle theme"
            title={isLightTheme ? "Switch to dark theme" : "Switch to light theme"}
          >
            {isLightTheme ? <FiMoon /> : <FiSun />}
            <span>{isLightTheme ? "Dark" : "Light"}</span>
          </button>
          <button
            className={`navbar-iconbtn${ticketAlert > 0 ? " navbar-iconbtn--alert" : ""}`}
            type="button"
            aria-label={ticketAlert > 0 ? `${ticketAlert} open support tickets` : "Notifications"}
            title={ticketAlert > 0 ? `${ticketAlert} open support ticket(s)` : "Notifications"}
            onClick={() => navigate("/dashboard/support")}
          >
            <FiBell />
            {ticketAlert > 0 && (
              <span className="navbar-iconbtn__badge">{ticketAlert > 9 ? "9+" : ticketAlert}</span>
            )}
          </button>
          <button className="navbar-iconbtn" type="button" aria-label="Help">
            <FiHelpCircle />
          </button>
          <button className="navbar-iconbtn" type="button" aria-label="Apps">
            <FiGrid />
          </button>
        </div>

        <div className="navbar-user">
          <div className="navbar-user__meta">
            <div className="navbar-user__name">{userName}</div>
            <div className="navbar-user__role">{typeLabel[userType] || userType}</div>
          </div>
          <div className="profile-wrapper" ref={dropdownRef}>
            <div
              className="profile-avatar"
              onClick={() => setDropdownOpen((prev) => !prev)}
              style={{ width: 28, height: 28, fontSize: 12 }}
            >
              {" "}
              <FiUser />
            </div>

            {dropdownOpen && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">{initials(userName)}</div>
                  <div className="dropdown-user-info">
                    <span className="dropdown-name">{userName}</span>
                    <span className="dropdown-role">
                      {typeLabel[userType] || userType}
                    </span>
                  </div>
                </div>
                <div className="dropdown-divider" />
                <button className="dropdown-item" onClick={handleViewProfile}>
                  <FiUser className="dropdown-icon" />
                  View Profile
                </button>
                <button className="dropdown-item logout" onClick={handleLogoutClick}>
                  <FiLogOut className="dropdown-icon" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* User Profile Dropdown */}
      </div>

      {/* Logout Confirmation Modal */}
      {logoutOpen &&
        createPortal(
          <div className="sb-logout-overlay">
            <div
              className="sb-logout-scrim"
              onClick={handleCloseLogout}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleCloseLogout()}
            />
            <div className="sb-logout-dialog">
              <div className="sb-logout-dialog__header">
                <h3 className="sb-logout-dialog__title">Logout</h3>
                <button
                  className="sb-logout-dialog__close"
                  onClick={handleCloseLogout}
                  type="button"
                  aria-label="Close"
                >
                  <FiX />
                </button>
              </div>
              <div className="sb-logout-dialog__body">
                <p className="sb-logout-dialog__text">
                  Are you sure you want to logout?
                </p>
                <p className="sb-logout-dialog__subtext">
                  You will be redirected to the login page.
                </p>
              </div>
              <div className="sb-logout-dialog__footer">
                <button
                  className="sb-logout-dialog__cancel"
                  type="button"
                  onClick={handleCloseLogout}
                >
                  Cancel
                </button>
                <button
                  className="sb-logout-dialog__confirm"
                  type="button"
                  onClick={handleConfirmLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </nav>
  );
};

export default Navbar;
